// Attendance for one event session, from the Event List row or a Regular Class
// List event row. Reads and saves through GET/PUT
// api/workshops/:id/attendance/:sessionKey (server V101,
// docs/events-types-sessions-contract.md C), the event analogue of
// SchoolProgramAttendanceDialog. The session key is a workshop_session_id, or
// `day` for an event without sessions (its one implicit session). Marking uses
// the class attendance dialog's Present / Absent buttons.
import DirectionsRunIcon from "@material-ui/icons/DirectionsRun";
import PanToolIcon from "@material-ui/icons/PanTool";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Badge, Button, Modal, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { shallowEqual, useSelector } from "react-redux";
import { formatSessionDate, formatSessionTimes, toHHMM, toIsoDate, todayIn } from "./eventSessions";

const sessionsUrl = "api/workshops/sessions/";
const attendeeDetailsUrl = "api/workshops/attendee-details/";
const DAY = "day";
const CANNOT_CLEAR = "A saved mark cannot be deleted, only changed";
const UNDO_HINT =
  "Tap the highlighted button again to undo a mark. A saved mark goes back to Unmarked, since marks cannot be deleted.";

const badgeStyle = (backgroundColor) => ({
  backgroundColor,
  color: "white",
  borderRadius: "5px",
  padding: "5px 8px",
  fontSize: "12px",
});
const PRESENT_BADGE = badgeStyle("#4BB543");
const ABSENT_BADGE = badgeStyle("#dc3545");
const UNMARKED_BADGE = badgeStyle("#a1a5b7");

// The session the sheet opens on: today's when there is one, otherwise the
// most recent one before today, otherwise the first; cancelled ones only when
// nothing else is left.
export const defaultSessionKey = (sessions, today) => {
  if (!sessions.length) return DAY;
  const live = sessions.filter((s) => Number(s.cancelled_ind) !== 1);
  const pool = live.length ? live : sessions;
  const past = pool.filter((s) => s.session_date <= today);
  const pick = past.length ? past[past.length - 1] : pool[0];
  return String(pick.workshop_session_id);
};

const sessionLabel = (s) =>
  `${formatSessionDate(s.session_date)} · ${formatSessionTimes(s.start_time, s.end_time)}${
    Number(s.cancelled_ind) === 1 ? " (cancelled)" : ""
  }`;

// row: the Event List row (workshop_id, workshop_name, startdate, start_time,
// end_time). initialSessionKey opens the dialog on that session; with
// lockSession the picker is hidden (and the sessions list not fetched), for
// callers that are already about one session, where row's date and times are
// that session's. onSaved runs after a successful save, before onHide.
export function EventAttendanceDialog({
  row,
  show,
  onHide,
  initialSessionKey,
  lockSession = false,
  onSaved,
}) {
  const authToken = useSelector((state) => state.auth.authToken, shallowEqual);
  const branch = useSelector((state) => state.auth.branches.currentBranch, shallowEqual);
  const headers = { "x-auth-token": authToken };
  const workshopId = row.workshop_id;

  // The picker's choices: the event's sessions, or its one implicit day.
  const [sessions, setSessions] = useState(null);
  const [sessionKey, setSessionKey] = useState("");
  const [sheet, setSheet] = useState(null); // GET data
  const [students, setStudents] = useState([]);
  // user_id -> the status saved on this sheet (the GET's), to send only what
  // changed and to stop a saved mark going back to no mark: the API has no
  // way to delete one, so undoing it sets the Unmarked status instead.
  const [savedMarks, setSavedMarks] = useState(new Map());
  const [comment, setComment] = useState("");
  const [pendingCount, setPendingCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [errorDetails, setErrorDetails] = useState([]);
  // Every sheet load takes a ticket; a reply whose ticket is no longer the
  // latest (session changed, dialog closed) is dropped.
  const loadTicket = useRef(0);

  const loadSheet = async (key) => {
    const ticket = ++loadTicket.current;
    setIsLoading(true);
    setError("");
    setErrorDetails([]);
    try {
      const res = await axios(`api/workshops/${workshopId}/attendance/${key}`, { headers });
      if (ticket !== loadTicket.current) return;
      const data = res.data.data || {};
      setSheet(data);
      setStudents(data.students || []);
      setSavedMarks(
        new Map(
          (data.students || [])
            .filter((s) => s.attendance_id != null)
            .map((s) => [s.user_id, s.attendance_id])
        )
      );
      setComment(data.comment || "");
      // An empty roster may only mean the website registrations are not
      // imported yet; say so rather than "no students". Not from a locked
      // dialog: that lookup is staff-only (it carries parent details) and the
      // class list opens it for Instructors too.
      if (!lockSession && !(data.students || []).length) {
        try {
          const details = await axios(attendeeDetailsUrl + workshopId, { headers });
          if (ticket === loadTicket.current)
            setPendingCount(details.data.results?.pending?.length || 0);
        } catch (e) {
          console.error("event attendees: ", e);
        }
      }
    } catch (e) {
      if (ticket !== loadTicket.current) return;
      setSheet(null);
      setStudents([]);
      setSavedMarks(new Map());
      setComment("");
      setError(e.response?.data?.message || "Could not load the attendance sheet.");
    } finally {
      if (ticket === loadTicket.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!show) {
      loadTicket.current++;
      setSessions(null);
      setSheet(null);
      setStudents([]);
      setComment("");
      setPendingCount(0);
      setError("");
      setErrorDetails([]);
      return undefined;
    }
    // A locked dialog needs no picker, so it skips the sessions list (whose
    // route Instructors may not be allowed on) and opens the sheet directly.
    if (lockSession && initialSessionKey != null) {
      const key = String(initialSessionKey);
      setSessions([]);
      setSessionKey(key);
      loadSheet(key);
      return undefined;
    }
    let cancelled = false;
    axios(sessionsUrl + workshopId, { headers })
      .then((res) => {
        if (cancelled) return;
        const list = res.data.results || [];
        setSessions(list);
        const wanted = initialSessionKey != null ? String(initialSessionKey) : null;
        const key =
          wanted &&
          (list.length
            ? list.some((s) => String(s.workshop_session_id) === wanted)
            : wanted === DAY)
            ? wanted
            : defaultSessionKey(list, todayIn(branch?.timezone));
        setSessionKey(key);
        loadSheet(key);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error("event sessions: ", e);
        setSessions([]);
        setError(e.response?.data?.message || "Could not load the event's sessions.");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, workshopId, authToken, initialSessionKey, lockSession]);

  // The statuses are found by name, never by id (ids differ per database).
  // The server sends Present, Absent, Unmarked; an older one that lacks any of
  // them gets the status select instead of the buttons.
  const statuses = sheet?.statuses || [];
  const statusId = (name) => statuses.find((s) => s.name === name)?.attendance_id;
  const presentId = statusId("Present");
  const absentId = statusId("Absent");
  const unmarkedId = statusId("Unmarked");
  const useButtons = presentId != null && absentId != null && unmarkedId != null;
  // A cancelled session reads only. A session after today saves like any
  // other: staff may mark attendance ahead of it.
  const cancelled = Number(sheet?.session?.cancelled_ind) === 1;
  const readOnly = cancelled;
  // With the buttons, anything but Present or Absent counts as unmarked, so an
  // older status still stored on a student is left for the coach to resolve.
  const isUnmarked = (s) =>
    useButtons
      ? s.attendance_id !== presentId && s.attendance_id !== absentId
      : s.attendance_id == null || (unmarkedId != null && s.attendance_id === unmarkedId);
  const unmarked = students.filter(isUnmarked).length;

  const setMark = (userId, attendanceId) =>
    setStudents((list) =>
      list.map((s) => (s.user_id === userId ? { ...s, attendance_id: attendanceId } : s))
    );

  // A Present / Absent button sets its status; tapped again while it is the
  // student's status, it undoes the mark: back to no mark when the mark was
  // only made here, to Unmarked when it is saved (it cannot be deleted).
  const toggleMark = (s, attendanceId) =>
    setMark(
      s.user_id,
      s.attendance_id !== attendanceId
        ? attendanceId
        : savedMarks.has(s.user_id)
        ? unmarkedId
        : null
    );

  const statusBadge = (s) => {
    if (s.attendance_id === presentId) return <Badge style={PRESENT_BADGE}>Present</Badge>;
    if (s.attendance_id === absentId) return <Badge style={ABSENT_BADGE}>Absent</Badge>;
    if (s.attendance_id == null || s.attendance_id === unmarkedId)
      return <Badge style={UNMARKED_BADGE}>Unmarked</Badge>;
    // A status the server no longer offers (older data).
    const name = statuses.find((st) => st.attendance_id === s.attendance_id)?.name;
    return (
      <Badge style={UNMARKED_BADGE} title={`Status id ${s.attendance_id}`}>
        {name || "Other status"}
      </Badge>
    );
  };

  const canSave = !!sheet && !isLoading && !isSaving && !readOnly && students.length > 0;

  // Only students whose status changed are sent: a mark counts as attendance
  // on the session (which then can't be deleted from the schedule), so
  // untouched students and marks undone in this dialog stay off the sheet
  // rather than going up as "Unmarked". Undoing a saved mark does send
  // Unmarked, the one way to take it back. The comment goes as typed
  // (trimmed): the server stores it as a parameter.
  const save = async () => {
    if (!canSave) return;
    setIsSaving(true);
    setError("");
    setErrorDetails([]);
    try {
      await axios.put(
        `api/workshops/${workshopId}/attendance/${sessionKey}`,
        {
          comment: comment.trim(),
          student_list: students
            .filter((s) => s.attendance_id != null && s.attendance_id !== savedMarks.get(s.user_id))
            .map((s) => ({ user_id: s.user_id, attendance_id: s.attendance_id })),
        },
        { headers }
      );
      setIsSaving(false);
      onSaved?.();
      onHide();
    } catch (e) {
      const body = e.response?.data;
      setError(body?.message || "Could not save attendance. Please try again.");
      setErrorDetails(
        Array.isArray(body?.errors)
          ? body.errors.map((d) => {
              const name = students.find((s) => s.user_id === d.user_id)?.student_name;
              return `${name || (d.user_id != null ? `#${d.user_id}` : "Entry")}: ${d.reason}`;
            })
          : []
      );
      setIsSaving(false);
    }
  };

  const day = {
    session_date: toIsoDate(row.startdate),
    start_time: toHHMM(row.start_time),
    end_time: toHHMM(row.end_time),
  };

  return (
    <Modal
      show={show}
      onHide={isSaving ? () => {} : onHide}
      backdrop={isSaving ? "static" : true}
      keyboard={!isSaving}
      aria-labelledby={`event-attendance-${workshopId}`}
      size="lg"
    >
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title id={`event-attendance-${workshopId}`}>
          Attendance — {row.workshop_name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {sessions === null ? (
          <div className="d-flex align-items-center text-muted">
            <Spinner animation="border" size="sm" className="mr-2" />
            Loading sessions…
          </div>
        ) : (
          <fieldset disabled={isLoading || isSaving}>
            <div className="form-group">
              <label htmlFor={`event-attendance-session-${workshopId}`}>Session</label>
              {lockSession ? (
                <p id={`event-attendance-session-${workshopId}`} className="form-control-plaintext">
                  {sessionLabel(sheet?.session || day)}
                </p>
              ) : sessions.length > 0 ? (
                <select
                  id={`event-attendance-session-${workshopId}`}
                  className="form-control"
                  style={{ maxWidth: 420 }}
                  value={sessionKey}
                  onChange={(e) => {
                    setSessionKey(e.target.value);
                    setPendingCount(0);
                    loadSheet(e.target.value);
                  }}
                >
                  {sessions.map((s) => (
                    <option key={s.workshop_session_id} value={String(s.workshop_session_id)}>
                      {sessionLabel(s)}
                    </option>
                  ))}
                </select>
              ) : (
                <p id={`event-attendance-session-${workshopId}`} className="form-control-plaintext">
                  {sessionLabel(sheet?.session || day)}
                  <span className="text-muted">
                    {" "}
                    (no sessions: the event&apos;s own date and times)
                  </span>
                </p>
              )}
            </div>

            {cancelled && (
              <div className="alert alert-custom alert-light-warning py-2 mb-3">
                <div className="alert-text">
                  This session is cancelled: its attendance is read-only.
                </div>
              </div>
            )}
            {error && (
              <div className="alert alert-danger py-2">
                {error}
                {errorDetails.length > 0 && (
                  <ul className="mb-0 mt-1 pl-4">
                    {errorDetails.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {isLoading ? (
              <div className="d-flex align-items-center text-muted py-3">
                <Spinner animation="border" size="sm" className="mr-2" />
                Loading the attendance sheet…
              </div>
            ) : sheet && students.length === 0 ? (
              <p className="text-muted">
                No registered students.
                {pendingCount > 0 &&
                  ` ${pendingCount} website registration${
                    pendingCount === 1 ? " is" : "s are"
                  } waiting to be imported; they can be marked once imported.`}
              </p>
            ) : (
              sheet && (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted">
                      Registered students only (live registrations).
                    </span>
                    {!readOnly && presentId != null && unmarked > 0 && (
                      <button
                        type="button"
                        className="btn btn-light-primary btn-sm"
                        onClick={() =>
                          setStudents((list) =>
                            list.map((s) => (isUnmarked(s) ? { ...s, attendance_id: presentId } : s))
                          )
                        }
                      >
                        Mark the {unmarked} unmarked present
                      </button>
                    )}
                  </div>
                  {useButtons && !readOnly && (
                    <p className="text-muted font-size-sm mb-2">{UNDO_HINT}</p>
                  )}
                  <table className="table table-sm table-vertical-center">
                    <thead>
                      <tr>
                        <th style={{ width: 90 }}>ID</th>
                        <th>Student</th>
                        {useButtons ? (
                          <>
                            <th style={{ width: 110 }} className="text-center">
                              Status
                            </th>
                            <th style={{ width: 130 }} className="text-center">
                              Mark
                            </th>
                          </>
                        ) : (
                          <th style={{ width: 220 }}>Status</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((s) => (
                        <tr key={s.user_id}>
                          <td>{s.user_id}</td>
                          <td>{s.student_name}</td>
                          {useButtons ? (
                            <>
                              <td className="text-center">{statusBadge(s)}</td>
                              <td className="text-center text-nowrap">
                                <OverlayTrigger
                                  overlay={
                                    <Tooltip id={`event-present-${s.user_id}`}>Student Present</Tooltip>
                                  }
                                >
                                  <Button
                                    variant={s.attendance_id === presentId ? "success" : "secondary"}
                                    className="mr-2 btn-sm"
                                    aria-label={`${s.student_name}: present`}
                                    aria-pressed={s.attendance_id === presentId}
                                    disabled={readOnly}
                                    onClick={() => toggleMark(s, presentId)}
                                  >
                                    <PanToolIcon fontSize="small" />
                                  </Button>
                                </OverlayTrigger>
                                <OverlayTrigger
                                  overlay={
                                    <Tooltip id={`event-absent-${s.user_id}`}>Student Absent</Tooltip>
                                  }
                                >
                                  <Button
                                    variant={s.attendance_id === absentId ? "danger" : "secondary"}
                                    className="btn-sm"
                                    aria-label={`${s.student_name}: absent`}
                                    aria-pressed={s.attendance_id === absentId}
                                    disabled={readOnly}
                                    onClick={() => toggleMark(s, absentId)}
                                  >
                                    <DirectionsRunIcon fontSize="small" />
                                  </Button>
                                </OverlayTrigger>
                              </td>
                            </>
                          ) : (
                            <td>
                              <select
                                className="form-control form-control-sm"
                                aria-label={`Status for ${s.student_name}`}
                                title={savedMarks.has(s.user_id) ? CANNOT_CLEAR : undefined}
                                value={s.attendance_id ?? ""}
                                disabled={readOnly}
                                onChange={(e) =>
                                  setMark(
                                    s.user_id,
                                    e.target.value === "" ? null : Number(e.target.value)
                                  )
                                }
                              >
                                <option value="" disabled={savedMarks.has(s.user_id)}>
                                  {savedMarks.has(s.user_id)
                                    ? `Not marked (${CANNOT_CLEAR.toLowerCase()})`
                                    : "Not marked"}
                                </option>
                                {statuses.map((st) => (
                                  <option key={st.attendance_id} value={st.attendance_id}>
                                    {st.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )
            )}

            {sheet && (
              <div className="form-group mt-4 mb-0">
                <label htmlFor={`event-attendance-comment-${workshopId}`}>Attendance comment</label>
                <textarea
                  id={`event-attendance-comment-${workshopId}`}
                  className="form-control"
                  rows={3}
                  placeholder="Comment"
                  value={comment}
                  disabled={readOnly}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>
            )}
          </fieldset>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-light btn-elevate"
          onClick={onHide}
          disabled={isSaving}
        >
          Close
        </button>
        <button
          type="button"
          className="btn btn-primary btn-elevate"
          onClick={save}
          disabled={!canSave}
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
